import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiUrl } from '../shared/models/apiurl.models';

/** SCRUM-88 — optional store identity on a seller. */
export interface StoreProfile {
  id: number;
  name: string;
  subtitle: string;
  description: string;
  logo: string | null;
  // SCRUM-96 additions
  phone: string;
  website: string;
  city: string;
  // SCRUM-260 / 261 / 264
  domains?: Array<{ category: string; name: string }>;
  service_areas?: string[];
  contact_person?: string;
  license?: {
    type: string;
    type_label: string;
    number: string;
    status: string;
    verified: boolean;
    verified_text: string;
  } | null;
  verified?: boolean;
  verified_text?: string;
}

export interface SellerRating {
  average: number | null;
  count: number;
}

/** SCRUM-87 — follow state as the profile reports it. */
export interface SellerFollow {
  // Spelled as SCRUM-92 defines them.
  followers_count: number;
  following_count: number;
  is_followed_by_me: boolean;
}

export interface SellerProfile {
  id: number;
  display_name: string;
  avatar: string | null;
  member_since: string | null;
  /**
   * Already resolved server-side against the subscription end date, so a
   * lapsed subscriber arrives as false. Do not re-derive from `is_vip`.
   */
  is_vip: boolean;
  /** SCRUM-259 — live VIP incl. grace period. */
  is_business?: boolean;
  store: StoreProfile | null;
  rating: SellerRating;
  stats: { active_ads: number };
  follow: SellerFollow;
}

export interface Paged<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface FollowResult {
  following: boolean;
  followers: number;
}

@Injectable({ providedIn: 'root' })
export class SellerService {
  constructor(private http: HttpClient, private apiUrl: ApiUrl) {}

  /**
   * Authenticated header. The profile endpoint itself is public, but sending
   * the token is what makes `follow.is_following` meaningful — the backend
   * resolves it by hand precisely so a missing or stale token degrades to
   * "not following" rather than a 401 on a public page.
   */
  private get authOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: this.apiUrl.getJWTToken(),
      }),
    };
  }

  getProfile(userId: number): Observable<SellerProfile> {
    return this.http.get<SellerProfile>(
      this.apiUrl.getSellerProfileUrl(userId),
      this.authOptions
    );
  }

  getAds(userId: number, page = 1): Observable<Paged<any>> {
    return this.http.get<Paged<any>>(
      `${this.apiUrl.getSellerAdsUrl(userId)}?page=${page}`,
      this.authOptions
    );
  }

  follow(userId: number): Observable<FollowResult> {
    return this.http.post<FollowResult>(
      this.apiUrl.getFollowUrl(userId),
      {},
      this.authOptions
    );
  }

  unfollow(userId: number): Observable<FollowResult> {
    return this.http.delete<FollowResult>(
      this.apiUrl.getFollowUrl(userId),
      this.authOptions
    );
  }
}
